import api from '@/services/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  eligibilityService,
  type EligibilityRulesParams,
  type MasterMappingsParams,
  type EligibilityRule,
  type CheckEligibilityRequest,
} from '@/services/eligibility';

export function useEligibilityRules(params?: EligibilityRulesParams) {
  return useQuery({
    queryKey: ['eligibility-rules', params],
    queryFn: () => eligibilityService.getRules(params),
  });
}

export function useCreateRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<EligibilityRule>) => eligibilityService.createRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eligibility-rules'] });
    },
  });
}

export function useUpdateRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<EligibilityRule> }) =>
      eligibilityService.updateRule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eligibility-rules'] });
    },
  });
}

export function useDeleteRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => eligibilityService.deleteRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eligibility-rules'] });
    },
  });
}

export function useBulkImportRules() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { rules: Partial<EligibilityRule>[] }) =>
      eligibilityService.bulkImportRules(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eligibility-rules'] });
    },
  });
}

export function useCheckEligibility() {
  return useMutation({
    mutationFn: (data: CheckEligibilityRequest) => eligibilityService.checkEligibility(data),
  });
}

export function useMasterMappings(params?: MasterMappingsParams, enabled = true) {
  return useQuery({
    queryKey: ['master-mappings', params],
    queryFn: () => eligibilityService.getMappings(params),
    enabled,
  });
}

export function useImportMappings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      dryRun,
      masterType,
    }: {
      file: File;
      dryRun: boolean;
      masterType?: string;
    }) => eligibilityService.importMappings(file, dryRun, masterType),
    onSuccess: (result) => {
      if (!result.dry_run && result.imported > 0) {
        queryClient.invalidateQueries({ queryKey: ['master-mappings'] });
      }
    },
  });
}

/** Every state, for the rule form's State dropdown (a rule's `state` is a state id). */
export function useEligibilityStates() {
  return useQuery({
    queryKey: ['eligibility-rules', 'states'],
    queryFn: async () => {
      const response = await api.get<{ results: { id: string; name: string }[] }>('/geography/states/', {
        params: { page_size: 200, is_active: true },
      });
      return response.data.results;
    },
    staleTime: 10 * 60 * 1000,
  });
}
